import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { App, Popover } from 'antd';
import type {
  InputNode,
  ShopDateValue,
  ShopInfo,
  ShopListFetcher,
} from '../../types';
import { usePopupHost } from '../../PopupHostContext';
import { RequiredMark } from '../RequiredMark';
import arrowIcon from './assets/arrow.svg';
import checkIcon from './assets/check.svg';
import closeIcon from './assets/close.svg';
import { TooltipIcon } from './TooltipIcon';
import {
  buildShopDateValue,
  parseShopDateValue,
  SHOP_DATE_RANGES,
  type ShopDateRange,
} from './utils';
import { parseStepsTooltip } from '../../utils/stepsTooltip';
import './index.css';

type Props = {
  node: InputNode;
  value: string[] | undefined;
  onChange: (key: string, value: ShopDateValue | []) => void;
  shopListFetch?: ShopListFetcher;
  disabled?: boolean;
  error?: string;
};

const createDraftShop = (
  selection: ReturnType<typeof parseShopDateValue>,
): ShopInfo | null => selection
  ? {
      shopId: selection.shopId,
      shopName: selection.shopName,
      platformName: '',
      orderAuthStatus: 1,
      promotionAuthStatus: 0,
      alipayAuthStatus: 0,
      insightAuthStatus: 0,
    }
  : null;

const resolveRange = (
  selection: ReturnType<typeof parseShopDateValue>,
): ShopDateRange | null => {
  if (!selection) return null;
  return SHOP_DATE_RANGES.find((range) => range.label === selection.rangeLabel) ?? null;
};

const normalizeTooltip = (tooltip: InputNode['tooltip']): string => {
  if (typeof tooltip !== 'string') return '';
  const text = tooltip.trim();
  return parseStepsTooltip(tooltip) ? '' : text;
};

export const ShopDateSelectNode: React.FC<Props> = React.memo(({
  node,
  value,
  onChange,
  shopListFetch,
  disabled,
  error,
}) => {
  const { message } = App.useApp();
  const popupHost = usePopupHost();
  const selection = useMemo(() => parseShopDateValue(value), [value]);
  const tooltip = normalizeTooltip(node.tooltip);
  const [open, setOpen] = useState(false);
  const [shops, setShops] = useState<ShopInfo[]>([]);
  const [draftShop, setDraftShop] = useState<ShopInfo | null>(() =>
    createDraftShop(selection));
  const [draftRange, setDraftRange] = useState<ShopDateRange | null>(() =>
    resolveRange(selection));
  const requestSeqRef = useRef(0);
  const requestingRef = useRef(false);

  useEffect(() => {
    if (open) return;
    setDraftShop(createDraftShop(selection));
    setDraftRange(resolveRange(selection));
  }, [open, selection]);

  useEffect(() => () => {
    requestSeqRef.current += 1;
    requestingRef.current = false;
  }, [node.key, shopListFetch]);

  const completeSelection = useCallback((shop: ShopInfo, range: ShopDateRange) => {
    if (!node.key || shop.promotionAuthStatus !== 1) return;
    onChange(node.key, buildShopDateValue(shop, range));
    setOpen(false);
  }, [node.key, onChange]);

  const handleShopSelect = useCallback((shop: ShopInfo) => {
    if (shop.promotionAuthStatus !== 1) return;
    setDraftShop(shop);
    if (draftRange) completeSelection(shop, draftRange);
  }, [completeSelection, draftRange]);

  const handleRangeSelect = useCallback((range: ShopDateRange) => {
    setDraftRange(range);
    if (draftShop) completeSelection(draftShop, range);
  }, [completeSelection, draftShop]);

  const requestShopsAndOpen = useCallback(async () => {
    if (disabled || requestingRef.current) return;
    if (!shopListFetch) {
      setShops([]);
      setOpen(true);
      return;
    }

    requestingRef.current = true;
    const requestSeq = ++requestSeqRef.current;
    try {
      const nextShops = await shopListFetch({ nodeKey: node.key ?? '' });
      if (requestSeq !== requestSeqRef.current) return;
      setShops(nextShops);
      setDraftShop((current) =>
        nextShops.find((shop) => shop.shopId === current?.shopId) ?? current);
      setOpen(true);
    } catch (requestError) {
      if (requestSeq !== requestSeqRef.current) return;
      const errorMessage = requestError instanceof Error && requestError.message.trim()
        ? requestError.message.trim()
        : '获取店铺列表失败';
      setShops([]);
      setOpen(false);
      message.error(errorMessage);
    } finally {
      if (requestSeq === requestSeqRef.current) requestingRef.current = false;
    }
  }, [disabled, message, node.key, shopListFetch]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      void requestShopsAndOpen();
      return;
    }
    requestSeqRef.current += 1;
    requestingRef.current = false;
    setOpen(false);
  }, [requestShopsAndOpen]);

  const handleClear = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!node.key || disabled) return;
    setDraftShop(null);
    setDraftRange(null);
    onChange(node.key, []);
    setOpen(false);
  }, [disabled, node.key, onChange]);

  const panel = (
    <div className="rte-shop-date-panel" onMouseDown={(event) => event.stopPropagation()}>
      <section className="rte-shop-date-column rte-shop-date-shop-column">
        <div className="rte-shop-date-heading">店铺</div>
        <div className="rte-shop-date-list">
          {shops.length > 0 ? shops.map((shop) => {
            const authorized = shop.promotionAuthStatus === 1;
            const selected = draftShop?.shopId === shop.shopId;
            return (
              <button
                key={shop.shopId}
                type="button"
                className={`rte-shop-date-item${selected ? ' is-selected' : ''}`}
                disabled={!authorized}
                onClick={() => handleShopSelect(shop)}
                title={shop.shopName}
              >
                <span className="rte-shop-date-item-label">{shop.shopName}</span>
                {!authorized && <span className="rte-shop-date-unauthorized">未授权</span>}
                {selected && <img src={checkIcon} alt="" />}
              </button>
            );
          }) : (
            <div className="rte-shop-date-status">暂无店铺</div>
          )}
        </div>
      </section>
      <section className="rte-shop-date-column rte-shop-date-range-column">
        <div className="rte-shop-date-heading">时间</div>
        <div className="rte-shop-date-list">
          {SHOP_DATE_RANGES.map((range) => {
            const selected = draftRange?.days === range.days;
            return (
              <button
                key={range.days}
                type="button"
                className={`rte-shop-date-item${selected ? ' is-selected' : ''}`}
                onClick={() => handleRangeSelect(range)}
              >
                <span className="rte-shop-date-item-label">{range.label}</span>
                {selected && <img src={checkIcon} alt="" />}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );

  return (
    <span className="rte-shop-date-node" contentEditable={false}>
      <RequiredMark node={node}>
        <Popover
          content={panel}
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomLeft"
          autoAdjustOverflow
          getPopupContainer={() => popupHost ?? document.body}
          overlayClassName="rte-shop-date-overlay"
          arrow={false}
        >
          <span
            className={`rte-shop-date-trigger${selection ? ' is-filled' : ''}${tooltip ? ' has-tooltip' : ''}${error ? ' has-error' : ''}${disabled ? ' is-disabled' : ''}`}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={node.placeholder ?? '请选择店铺和日期'}
            aria-expanded={open}
            onKeyDown={(event) => {
              if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                handleOpenChange(!open);
              }
            }}
          >
            {selection ? (
              <>
                <span className="rte-shop-date-value" title={selection.shopName}>
                  {selection.shopName}
                </span>
                <span className="rte-shop-date-dot" />
                <span className="rte-shop-date-range-label">{selection.rangeLabel}</span>
                <span
                  className="rte-shop-date-clear"
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-label="清空店铺和时间"
                  onClick={handleClear}
                >
                  <img src={closeIcon} alt="" />
                </span>
              </>
            ) : (
              <>
                {tooltip ? (
                  <span className="rte-shop-date-empty-content">
                    <span className="rte-shop-date-placeholder">
                      {node.placeholder ?? '请选择店铺和日期'}
                    </span>
                    <TooltipIcon tooltip={tooltip} />
                  </span>
                ) : (
                  <span className="rte-shop-date-placeholder">
                    {node.placeholder ?? '请选择店铺和日期'}
                  </span>
                )}
                <img className="rte-shop-date-arrow" src={arrowIcon} alt="" />
              </>
            )}
          </span>
        </Popover>
      </RequiredMark>
    </span>
  );
});

ShopDateSelectNode.displayName = 'ShopDateSelectNode';
